// Phase 1A Phase 9C.1 — Status pill style helpers.
//
// Reusable CSSProperties exports for color-coded status chips. Two
// families:
//   - Full-saturation pills (filled background, light text) — for
//     primary status emphasis (e.g., "Won", "Stuck", "Active").
//   - Outline pills (transparent bg, colored border + text) — for
//     secondary or table-cell status emphasis where filled pills
//     would compete with the row background.
//
// Trust contract:
//   - Token-only. No raw hex/rgb.
//   - No copy. No icons. No component imports.
//   - Pill colors map to verified state — they don't decorate.
//   - Adoption begins in 9C.3+. No UI consumes these in 9C.1.

import type { CSSProperties } from "react";

// ─── Base pill chrome shared across variants ────────────────────

export const pillBase: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "var(--sz-space-1)",
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  padding: "3px 10px",
  borderRadius: "var(--sz-radius-sm)",
  border: "1px solid transparent",
  whiteSpace: "nowrap",
  fontWeight: 600,
};

// ─── Full-saturation filled pills ───────────────────────────────

export const pillSuccess: CSSProperties = {
  ...pillBase,
  background: "var(--sz-success-bg)",
  color: "var(--sz-success-text)",
  border: "1px solid var(--sz-success)",
};

export const pillAmber: CSSProperties = {
  ...pillBase,
  background: "var(--sz-amber-bg)",
  color: "var(--sz-amber-text)",
  border: "1px solid var(--sz-amber)",
};

export const pillDanger: CSSProperties = {
  ...pillBase,
  background: "var(--sz-danger-bg)",
  color: "var(--sz-danger-text)",
  border: "1px solid var(--sz-danger)",
};

export const pillInfo: CSSProperties = {
  ...pillBase,
  background: "var(--sz-info-bg)",
  color: "var(--sz-info-text)",
  border: "1px solid var(--sz-info)",
};

export const pillNeutral: CSSProperties = {
  ...pillBase,
  background: "var(--sz-bg-surface-2)",
  color: "var(--sz-text-secondary)",
  border: "1px solid var(--sz-border-default)",
};

// ─── Outline pills (transparent, colored ring + text) ──────────

export const pillSuccessOutline: CSSProperties = {
  ...pillBase,
  background: "var(--sz-success-dim)",
  color: "var(--sz-success-text)",
  border: "1px solid var(--sz-success)",
};

export const pillAmberOutline: CSSProperties = {
  ...pillBase,
  background: "var(--sz-amber-dim)",
  color: "var(--sz-amber-text)",
  border: "1px solid var(--sz-amber)",
};

export const pillDangerOutline: CSSProperties = {
  ...pillBase,
  background: "var(--sz-danger-dim)",
  color: "var(--sz-danger-text)",
  border: "1px solid var(--sz-danger)",
};

export const pillInfoOutline: CSSProperties = {
  ...pillBase,
  background: "var(--sz-info-dim)",
  color: "var(--sz-info-text)",
  border: "1px solid var(--sz-info)",
};

// ─── Size modifiers (compose via spread) ────────────────────────

export const pillSizeLarge: CSSProperties = {
  fontSize: "var(--sz-mono-sm)",
  padding: "5px 14px",
  letterSpacing: "2px",
};

export const pillSizeSmall: CSSProperties = {
  fontSize: "9px",
  padding: "2px 7px",
  letterSpacing: "1px",
};
